import { API_PREFIX, apiFetch, apiGet, type Envelope, type Page } from '@/api/client'
import { clearTokens, getRefreshToken, setTokens } from '@/features/auth/session'

export interface Role {
  slug: string
  name: string
  description: string | null
}

export interface User {
  id: string
  email: string
  full_name: string
  phone: string | null
  is_active: boolean
  roles: Role[]
  /** `null` means "on the company rate", which is not the same as zero. */
  commission_rate_pct: string | null
  last_login_at: string | null
  created_at: string
}

/** `/auth/me`. Permission codes are `resource:action`, e.g. `users:create`. */
export interface CurrentUser extends User {
  permissions: string[]
}

interface TokenPair {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

export async function login(email: string, password: string): Promise<void> {
  const body = await apiFetch<Envelope<TokenPair>>(`${API_PREFIX}/auth/login`, {
    method: 'POST',
    anonymous: true,
    body: JSON.stringify({ email, password }),
  })
  setTokens(body.data)
}

/**
 * Ends the session server-side, then locally.
 *
 * The local clear runs even if the call fails. A user who clicks "sign out" on a flaky
 * connection must not be left holding a live token because the request timed out.
 */
export async function logout(): Promise<void> {
  const refresh_token = getRefreshToken()
  try {
    if (refresh_token) {
      await apiFetch(`${API_PREFIX}/auth/logout`, {
        method: 'POST',
        anonymous: true,
        body: JSON.stringify({ refresh_token }),
      })
    }
  } catch {
    /* already expired or unreachable -- signing out locally is still correct */
  } finally {
    clearTokens()
  }
}

export const getCurrentUser = () => apiGet<CurrentUser>(`${API_PREFIX}/auth/me`)

export interface CurrentUserUpdateInput {
  full_name?: string
  phone?: string | null
}

export const updateCurrentUser = (input: CurrentUserUpdateInput) =>
  apiGet<CurrentUser>(`${API_PREFIX}/auth/me`, { method: 'PATCH', body: JSON.stringify(input) })

export const listUsers = (page = 1) => apiFetch<Page<User>>(`${API_PREFIX}/users?page=${page}`)

export const listRoles = () => apiGet<Role[]>(`${API_PREFIX}/roles`)

/**
 * The PRD section 9 grid, read from the database rather than from a constant in either
 * codebase.
 *
 * `cells` is the legend letter a role holds on a resource, or `null` where it holds a
 * combination the legend has no letter for -- possible now that the grid is editable, and
 * better shown as "custom" than rounded to the nearest letter.
 */
export interface MatrixResource {
  resource: string
  label: string
  actions: Record<string, string[]>
  cells: Record<string, string | null>
}

export interface RoleDetail extends Role {
  id: string
  is_system: boolean
  permissions: string[]
  user_count: number
  /** False for Super Admin: it is the way back in, so it is not editable from here. */
  editable: boolean
}

export interface PermissionMatrix {
  legend: Record<string, string>
  legend_actions: Record<string, string[]>
  actions: string[]
  roles: RoleDetail[]
  resources: MatrixResource[]
}

export const getPermissionMatrix = () => apiGet<PermissionMatrix>(`${API_PREFIX}/roles/matrix`)

/**
 * Replaces a role's permissions with exactly this set.
 *
 * Whole-set, not add-and-remove: two admins saving the same screen land on one of their
 * two answers rather than on the union of them, which is nobody's.
 */
export const setRolePermissions = (roleId: string, permissions: string[]) =>
  apiGet<RoleDetail>(`${API_PREFIX}/roles/${roleId}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ permissions }),
  })

export async function changePassword(
  current_password: string,
  new_password: string,
): Promise<void> {
  await apiFetch(`${API_PREFIX}/auth/change-password`, {
    method: 'POST',
    body: JSON.stringify({ current_password, new_password }),
  })
}

export async function requestPasswordReset(email: string): Promise<string> {
  const body = await apiGet<{ message: string }>(`${API_PREFIX}/auth/password-reset/request`, {
    method: 'POST',
    anonymous: true,
    body: JSON.stringify({ email }),
  })
  return body.message
}

export async function confirmPasswordReset(token: string, new_password: string): Promise<string> {
  const body = await apiGet<{ message: string }>(`${API_PREFIX}/auth/password-reset/confirm`, {
    method: 'POST',
    anonymous: true,
    body: JSON.stringify({ token, new_password }),
  })
  return body.message
}

export interface UserCreateInput {
  email: string
  full_name: string
  password: string
  phone?: string | null
  roles: string[]
  /** Omit, or send null, to leave them on the company rate. */
  commission_rate_pct?: string | null
}

/** Every field optional: the API patches only what it is sent. */
export interface UserUpdateInput {
  full_name?: string
  phone?: string | null
  is_active?: boolean
  roles?: string[]
  /** An explicit null clears the override; leaving the key out changes nothing. */
  commission_rate_pct?: string | null
}

export const createUser = (input: UserCreateInput) =>
  apiGet<User>(`${API_PREFIX}/users`, { method: 'POST', body: JSON.stringify(input) })

export const updateUser = (id: string, input: UserUpdateInput) =>
  apiGet<User>(`${API_PREFIX}/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) })

/**
 * Deactivates the account -- the server does not delete the row.
 *
 * The wording in the UI must match: a user tied to years of orders and approvals cannot be
 * removed without taking the audit trail with them.
 */
export const deactivateUser = (id: string) =>
  apiGet<{ message: string }>(`${API_PREFIX}/users/${id}`, { method: 'DELETE' })
